import React, { useRef, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import RpFiltersTabsList from 'components/commons/RpFiltersTabList';
import RpFiltersScrollContainer from 'components/commons/RpFiltersScrollContainer';
import RpFiltersSlidePanel from 'components/commons/RpFiltersSlidePanel';

function RpFiltersModal(props) {
    const boxRefs = useRef([]);
    const scrollContainerRef = useRef(null);
    const [slidePaneOpen, setSlidePaneOpen] = useState(false);
    const [scrollContainerReady, setScrollContainerReady] = useState(false);

    useEffect(() => {
        if (!props.open) return;
        const container = scrollContainerRef.current;
        if (!container) return;

        let debounceTimeout = null;

        const handleScroll = () => {
            const containerHeight = container.offsetHeight;
            const containerScroll = container.scrollTop;
            const scrollHeight = container.scrollHeight;

            let panelVaryingHeight = 0;
            let intersectingIdx = 0;
            const lastIdx = boxRefs.current.length - 1;

            for (let idx = 0; idx < boxRefs.current.length; idx++) {
                const child = boxRefs.current[idx];
                if (!child) continue;

                // Handle last child not reaching scroll threshold
                if ((scrollHeight === Math.round(containerScroll + containerHeight)) && intersectingIdx !== lastIdx) {
                    intersectingIdx = lastIdx;
                    break;
                } else if (containerScroll < (panelVaryingHeight + child.offsetHeight) - (containerHeight * 0.5)) {
                    intersectingIdx = idx;
                    break;
                } else {
                    panelVaryingHeight += child.offsetHeight;
                }
            }

            // Handle top edge case
            if (containerScroll === 0) {
                intersectingIdx = 0;
            }

            // Handle disabled last tab
            if (intersectingIdx === lastIdx && (props.tabs?.[lastIdx]?.disabled || props.tabs?.length === 1)) {
                return;
            }

            if (props.tabs?.[intersectingIdx]?.value !== props.tabIndex && props.open) {
                props.onTabChange(props.tabs[intersectingIdx].value);
            }
        };

        const debouncedScroll = () => {
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
            debounceTimeout = setTimeout(handleScroll, 10);
        };

        container.addEventListener('scroll', debouncedScroll);

        return () => {
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
            container.removeEventListener('scroll', debouncedScroll);
        };
    }, [props.open, scrollContainerReady, props.tabIndex]);


    const onTabChange = (event, newValue) => {
        const idx = props.tabs.findIndex(tab => tab.value === newValue);
        if (idx !== -1 && boxRefs.current[idx]) {
            boxRefs.current[idx].scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        props.onTabChange(newValue);
    };

    const onClose = () => {
        props.onClose();
        props.onTabChange(props.tabs[0].value);
        setSlidePaneOpen(false);
        setScrollContainerReady(false);
    };

    const onSlidePanelClose = () => {
        // Timeout matches the slide animation duration 500ms
        setTimeout(() => {
            props?.onSlidePanelClose?.();
        }, 500);
        setSlidePaneOpen(false);
    };

    return (
        <Dialog
            open={props.open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                Filters
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ ml: 2 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    paddingLeft: 2,
                    paddingRight: 0,
                    paddingBottom: 0,
                    overflow: 'hidden',
                }}
            >
                <Box sx={{display: 'flex', minHeight: 300}}>
                    <RpFiltersTabsList
                        tabs={props.tabs}
                        tabIndex={props.tabIndex}
                        onTabChange={onTabChange}
                        featureLocked={props.featureLocked}
                    />
                    <Box
                        sx={{
                            flexGrow: 1,
                            position: 'relative',
                            overflow: 'hidden',
                            scrollBehavior: 'smooth',
                        }}
                    >
                        <RpFiltersScrollContainer
                            ref={scrollContainerRef}
                            boxRefs={boxRefs}
                            slidePaneOpen={slidePaneOpen}
                            setSlidePaneOpen={setSlidePaneOpen}
                            setScrollContainerReady={setScrollContainerReady}
                        >
                            {props.children}
                        </RpFiltersScrollContainer>
                        <RpFiltersSlidePanel
                            slidePaneOpen={slidePaneOpen}
                            slidePanelSlot={props.slidePanelSlot}
                            onSlidePanelClose={onSlidePanelClose}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{borderTop: 1, borderColor: 'divider'}}>
                {props.actionButtons}
            </DialogActions>
        </Dialog>
    );
}

RpFiltersModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
            disabled: PropTypes.bool,
            hasEntitlements: PropTypes.bool,
        })
    ).isRequired,
    tabIndex: PropTypes.any,
    onTabChange: PropTypes.func.isRequired,
    actionButtons: PropTypes.node,
    children: PropTypes.node,
    featureLocked: PropTypes.bool,
    slidePanelSlot: PropTypes.node,
    onSlidePanelClose: PropTypes.func,
};

export default React.memo(RpFiltersModal);